import { memo, useCallback, useState } from 'react';

import { Tasks } from '@air/api';
import { DownloadIcon } from '@air/design-icons';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { workspaceAgentPanelPill } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DownloadFile } from '~/utils/DownloadFile';

export type PrivateWorkspaceAgentPanelMessageContentExporterMonikerProps = {
  taskId: string;
};

/**
 * Each click asks the task for a fresh signed link (valid about a day), so the chip keeps working
 * for as long as the zip exists in the downloads bucket (about 7 days). The agent only mints the
 * task; the file itself is fetched here, and only the task's creator is allowed to download it.
 *
 * Handoff prototype: no preview card, no size or file-count label, no expired-state styling.
 * The endpoint does not check that the file still exists, so an expired zip surfaces as a failed
 * download rather than a 404 here.
 */
export const PrivateWorkspaceAgentPanelMessageContentExporterMoniker = memo(
  ({ taskId }: PrivateWorkspaceAgentPanelMessageContentExporterMonikerProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { showToast } = useToasts();
    const [isDownloading, setIsDownloading] = useState(false);

    const workspaceId = currentWorkspace?.id;

    const handleDownload = useCallback(async () => {
      if (!workspaceId || isDownloading) {
        return;
      }

      setIsDownloading(true);

      try {
        const { downloadUrl } = await Tasks.ContentExporter.getDownloadUrl({ taskId, workspaceId });
        DownloadFile(downloadUrl);
      } catch (error) {
        reportErrorToBugsnag({ error, context: 'Workspace agent content exporter moniker download failed' });
        showToast('This download is no longer available. Ask the agent to export the assets again.', {
          color: 'red',
        });
      } finally {
        setIsDownloading(false);
      }
    }, [isDownloading, showToast, taskId, workspaceId]);

    return (
      <span className="inline-flex max-w-full items-center align-middle leading-none">
        <button
          aria-busy={isDownloading}
          className={workspaceAgentPanelPill({
            className: 'shrink-0',
            hasLeadingThumbnail: true,
            isInteractive: true,
          })}
          data-testid="WORKSPACE_AGENT_MESSAGE_CONTENT_EXPORTER_PILL"
          disabled={!workspaceId}
          onClick={handleDownload}
          type="button"
        >
          <span
            className="flex size-[18px] shrink-0 items-center justify-center rounded-2 bg-gray-3 text-gray-11"
            data-testid="WORKSPACE_AGENT_MESSAGE_CONTENT_EXPORTER_PILL_LEADING"
          >
            <DownloadIcon aria-hidden data-testid="WORKSPACE_AGENT_MESSAGE_CONTENT_EXPORTER_PILL_ICON" size="xs" />
          </span>
          <span className="truncate whitespace-nowrap">{isDownloading ? 'Preparing…' : 'Download zip'}</span>
        </button>
      </span>
    );
  },
);

PrivateWorkspaceAgentPanelMessageContentExporterMoniker.displayName =
  'PrivateWorkspaceAgentPanelMessageContentExporterMoniker';
